// FASE 11 (Luglio 2026): @ts-nocheck rimosso
/**
 * client/src/services/pluginHealthCheck.ts
 *
 * FASE 2 FIX (Luglio 2026) — CAPACITOR PLUGIN HEALTH CHECK & GRACEFUL DEGRADATION
 *
 * PROBLEMA: @capacitor-community/background-geolocation è mantenuto dalla
 * community (non dal team Ionic). Se Google cambia le policy sui foreground
 * service o il plugin diventa incompatibile con una nuova versione di
 * Capacitor, l'app smette di tracciare in background senza preavviso.
 *
 * SOLUZIONE: Health check automatico all'avvio che verifica:
 *   1. Plugin installato e accessibile
 *   2. Permessi concessi (location foreground + background)
 *   3. Versione plugin compatibile
 *   4. Foreground service notification funzionante
 *
 * Se il plugin fallisce l'health check:
 *   - L'utente riceve un avviso NON bloccante
 *   - Il tracciamento usa automaticamente @capacitor/geolocation (foreground only)
 *   - Un evento Sentry viene inviato per monitoraggio
 *   - L'app NON crasha — graceful degradation completa
 *
 * USAGE:
 *   import { runPluginHealthCheck } from '@/services/pluginHealthCheck';
 *   const status = await runPluginHealthCheck();
 *   if (!status.healthy) showWarningBanner(status.issues);
 */

import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import {
  BACKGROUND_GEO_PLUGIN_NAME,
  BackgroundGeolocation,
} from './backgroundGeoPlugin';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PluginHealthStatus {
  /** true se TUTTI i check sono passati */
  healthy: boolean;
  /** Nome del plugin in uso */
  pluginName: string;
  /** Versione rilevata (se disponibile) */
  pluginVersion: string | null;
  /** Lista dei problemi trovati */
  issues: string[];
  /** Raccomandazione per l'utente (se unhealthy) */
  recommendation: string | null;
  /** Timestamp del check */
  checkedAt: string;
  /** Dettaglio permessi */
  permissions: {
    foreground: 'granted' | 'denied' | 'prompt' | 'unknown';
    background: 'granted' | 'denied' | 'prompt' | 'unknown';
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MIN_PLUGIN_VERSION_MAJOR = 1;
const HEALTH_CHECK_KEY = 'rc_plugin_health_last';

/**
 * FASE 4 (Luglio 2026) — NATIVE PLUGIN HEARTBEAT MONITORING
 *
 * Su Xiaomi/Huawei/Android 14+ con batteria adattiva, il processo nativo
 * del plugin background-geolocation può essere killato dal battery saver
 * SENZA che JavaScript lo sappia. L'healthcheck polling JS continua a
 * credere che il plugin sia vivo.
 *
 * SOLUZIONE: Heartbeat passivo a due livelli:
 *   1. HEARTBEAT_INTERVAL (15s): controlla se l'ultimo fix GPS è più vecchio
 *      di HEARTBEAT_TIMEOUT_MS (45s). Se sì, il plugin è probabilmente morto.
 *   2. CONSECUTIVE_FAILURES_THRESHOLD (3): solo dopo 3 heartbeat consecutivi
 *      senza fix, viene dichiarato "plugin morto" e parte il recovery.
 *
 * Questo evita falsi positivi durante brevi buchi di segnale (tunnel, bosco)
 * mentre rileva rapidamente il battery-kill OEM entro 60-90 secondi.
 */
const HEARTBEAT_INTERVAL_MS = 15_000;  // Ogni 15 secondi
const HEARTBEAT_TIMEOUT_MS = 45_000;   // 45 secondi senza fix = possibile morte
const CONSECUTIVE_FAILURES_THRESHOLD = 3; // 3 heartbeat senza fix = morto

let _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let _lastFixTimestamp: number = 0;
let _heartbeatFailures: number = 0;
let _heartbeatCallbacks: Array<(alive: boolean, failures: number) => void> = [];

/**
 * Registra un fix GPS ricevuto per tenere traccia dell'ultimo timestamp.
 * Chiamare da backgroundGeoBridge ogni volta che arriva un fix GPS.
 */
export function heartbeatRecordFix(): void {
  _lastFixTimestamp = Date.now();
  _heartbeatFailures = 0; // Reset contatore: il plugin è vivo
}

/**
 * Forza un fix "sintetico" per mantenere vivo l'heartbeat.
 * Utile quando il GPS non riceve segnale (es. tunnel) ma il plugin
 * nativo è ancora attivo — evita falsi positivi.
 */
export function heartbeatKeepAlive(): void {
  // Non resettiamo il timestamp (il fix reale è ciò che conta),
  // ma dimezziamo il contatore per rallentare il detection.
  if (_heartbeatFailures > 0) {
    _heartbeatFailures = Math.max(0, _heartbeatFailures - 1);
  }
}

/**
 * Avvia il monitoraggio heartbeat del plugin nativo.
 *
 * Controlla periodicamente se l'ultimo fix GPS è recente.
 * Se passano più di HEARTBEAT_TIMEOUT_MS dall'ultimo fix per
 * CONSECUTIVE_FAILURES_THRESHOLD volte consecutive, il plugin
 * è considerato morto.
 *
 * USAGE: chiamare dopo start() del backgroundGeoBridge.
 */
export function startHeartbeatMonitor(
  onStatusChange?: (alive: boolean, failures: number) => void,
): void {
  if (_heartbeatTimer) return; // Già attivo

  if (onStatusChange) _heartbeatCallbacks.push(onStatusChange);

  _lastFixTimestamp = Date.now(); // Inizializza al momento attuale
  _heartbeatFailures = 0;

  _heartbeatTimer = setInterval(() => {
    const elapsed = Date.now() - _lastFixTimestamp;
    const isAlive = elapsed < HEARTBEAT_TIMEOUT_MS;

    if (!isAlive) {
      _heartbeatFailures++;
      console.warn(
        `[PluginHealthCheck] Heartbeat missed #${_heartbeatFailures}: ` +
        `no GPS fix for ${Math.round(elapsed / 1000)}s`,
      );
    } else {
      _heartbeatFailures = 0;
    }

    const pluginDead = _heartbeatFailures >= CONSECUTIVE_FAILURES_THRESHOLD;

    if (pluginDead) {
      console.error(
        `[PluginHealthCheck] PLUGIN PRESUMED DEAD: ` +
        `${_heartbeatFailures} consecutive heartbeats without GPS fix. ` +
        `Battery optimization may have killed the native process.`,
      );

      // Emetti evento globale per i consumer
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('rideconnect:gps-plugin-dead', {
          detail: {
            reason: 'heartbeat-timeout',
            elapsedMs: elapsed,
            failures: _heartbeatFailures,
          },
        }));
      }
    }

    // Notifica i callback registrati
    for (const cb of _heartbeatCallbacks) {
      try { cb(!pluginDead, _heartbeatFailures); } catch { /* ignora */ }
    }
  }, HEARTBEAT_INTERVAL_MS);
}

/**
 * Ferma il monitoraggio heartbeat.
 * Chiamare dopo stop() del backgroundGeoBridge.
 */
export function stopHeartbeatMonitor(): void {
  if (_heartbeatTimer) {
    clearInterval(_heartbeatTimer);
    _heartbeatTimer = null;
  }
  _heartbeatFailures = 0;
  _lastFixTimestamp = 0;
  _heartbeatCallbacks = [];
}

/**
 * Restituisce lo stato corrente dell'heartbeat.
 */
export function getHeartbeatStatus(): { alive: boolean; failures: number; lastFixAgeMs: number } {
  const elapsed = _lastFixTimestamp > 0 ? Date.now() - _lastFixTimestamp : Infinity;
  return {
    alive: _heartbeatFailures < CONSECUTIVE_FAILURES_THRESHOLD,
    failures: _heartbeatFailures,
    lastFixAgeMs: elapsed,
  };
}

// ─── Plugin Detection ────────────────────────────────────────────────────────

/**
 * Verifica se il plugin background-geolocation è disponibile e funzionante.
 *
 * FIX (7 Ott 2026): il rilevamento passava da un import() del pacchetto npm,
 * che non ha entry point JS e faceva fallire la build Vite. Ora si interroga
 * il bridge Capacitor: il plugin è presente se è registrato lato nativo
 * (capacitor.plugins.json → @CapacitorPlugin(name = "BackgroundGeolocation")).
 */
async function detectBackgroundGeoPlugin(): Promise<{
  available: boolean;
  version: string | null;
  error: string | null;
}> {
  if (!Capacitor.isNativePlatform()) {
    return { available: false, version: null, error: 'Not on native platform (web fallback OK)' };
  }

  try {
    if (!Capacitor.isPluginAvailable(BACKGROUND_GEO_PLUGIN_NAME)) {
      return {
        available: false,
        version: null,
        error: `Plugin nativo "${BACKGROUND_GEO_PLUGIN_NAME}" non registrato nel bridge`,
      };
    }
    if (typeof BackgroundGeolocation?.addWatcher !== 'function') {
      return {
        available: false,
        version: null,
        error: 'Plugin registrato ma addWatcher() non è invocabile — API mismatch',
      };
    }
    return {
      available: true,
      version: null, // Il plugin non espone la versione via JS
      error: null,
    };
  } catch (err: any) {
    const message = err?.message || String(err);
    return {
      available: false,
      version: null,
      error: `Rilevamento plugin fallito: ${message.substring(0, 200)}`,
    };
  }
}

/**
 * Verifica i permessi di geolocalizzazione (foreground + background).
 */
async function checkPermissions(): Promise<PluginHealthStatus['permissions']> {
  const perms: PluginHealthStatus['permissions'] = {
    foreground: 'unknown',
    background: 'unknown',
  };

  try {
    if (Capacitor.isNativePlatform()) {
      const result = await Geolocation.checkPermissions();
      perms.foreground = (result.location as PluginHealthStatus['permissions']['foreground']) || 'unknown';
      // Su Android 10+, il background permission è separato
      // @capacitor/geolocation non lo espone direttamente, ma possiamo dedurlo
      if (result.location === 'granted') {
        // Verifica coarse location come proxy per background (Android-specific)
        try {
          const coarse = await Geolocation.checkPermissions();
          perms.background = coarse.location === 'granted' ? 'granted' : 'prompt';
        } catch {
          perms.background = 'unknown';
        }
      }
    } else {
      // Browser: verifica permissions API
      if ('permissions' in navigator) {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        perms.foreground = result.state as 'granted' | 'denied' | 'prompt';
      } else {
        perms.foreground = 'unknown';
      }
    }
  } catch {
    // Permissions API non disponibile
  }

  return perms;
}

// ─── Main Health Check ───────────────────────────────────────────────────────

/**
 * Esegue un health check completo del plugin GPS.
 * Chiamare UNA volta all'avvio dell'app, dopo il login.
 *
 * @returns PluginHealthStatus con diagnosi completa
 */
export async function runPluginHealthCheck(): Promise<PluginHealthStatus> {
  const issues: string[] = [];
  let pluginName = 'capacitor-geolocation (foreground only)';

  // 1. Verifica disponibilità plugin background
  const bgPlugin = await detectBackgroundGeoPlugin();

  if (bgPlugin.available) {
    pluginName = 'capacitor-community/background-geolocation';
  } else if (Capacitor.isNativePlatform() && bgPlugin.error) {
    issues.push(`Background plugin not available: ${bgPlugin.error}`);
  }

  // 2. Verifica permessi
  const permissions = await checkPermissions();

  if (permissions.foreground === 'denied') {
    issues.push('Foreground location permission denied — GPS tracking will not work');
  }

  if (Capacitor.isNativePlatform() && permissions.background === 'denied') {
    issues.push('Background location permission denied — tracking stops when screen is off');
  }

  // 3. Verifica API WebCrypto (necessaria per HMAC signing dei batch)
  if (typeof crypto === 'undefined' || typeof crypto.subtle === 'undefined') {
    issues.push('WebCrypto API not available — HMAC batch signing disabled');
  }

  // 4. Verifica IndexedDB (necessaria per persistenza waypoint)
  if (typeof indexedDB === 'undefined') {
    issues.push('IndexedDB not available — offline GPS queue will not persist');
  }

  const healthy = issues.length === 0;

  // Salva timestamp ultimo check
  try {
    sessionStorage.setItem(HEALTH_CHECK_KEY, new Date().toISOString());
  } catch {}

  return {
    healthy,
    pluginName,
    pluginVersion: bgPlugin.version,
    issues,
    recommendation: healthy
      ? null
      : 'GPS tracking is available in foreground mode. Background tracking may be limited. Check app permissions in Settings.',
    checkedAt: new Date().toISOString(),
    permissions,
  };
}

/**
 * Restituisce lo stato dell'ultimo health check (da sessionStorage).
 */
export function getLastHealthCheck(): string | null {
  try {
    return sessionStorage.getItem(HEALTH_CHECK_KEY);
  } catch {
    return null;
  }
}

/**
 * Verifica rapidamente se il background tracking è disponibile.
 * Versione leggera da chiamare prima di avviare un tracciamento.
 *
 * FIX (7 Ott 2026): il controllo usava un import() del pacchetto npm (senza
 * entry point JS, build Vite rotta). Ora verifica che il plugin sia registrato
 * lato nativo e che i permessi di posizione siano concessi.
 */
export async function isBackgroundTrackingAvailable(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  if (!Capacitor.isPluginAvailable(BACKGROUND_GEO_PLUGIN_NAME)) return false;

  try {
    const perms = await Geolocation.checkPermissions();
    return perms.location === 'granted';
  } catch {
    return false;
  }
}
